import Raf from "quark-raf";
import ScrollCall from "../helpers/ScrollCall";
import { getScrollTop } from "../utils/dom";
import { mapRange, clamp } from "../utils/maths";

export default class View {
    constructor($el, id) {
        // Parameters
        this.$view = $el;
        this.id = id;

        // Get data attributes
        this.intersectionStart = $el.dataset["nlsStart"]
            ? parseInt($el.dataset["nlsStart"], 10)
            : 0;
        this.intersectionEnd = $el.dataset["nlsEnd"]
            ? parseInt($el.dataset["nlsEnd"], 10)
            : 0;
        this.progressStart = $el.dataset["nlsProgressStart"]
            ? parseInt($el.dataset["nlsProgressStart"], 10)
            : 0;
        this.progressEnd = $el.dataset["nlsProgressEnd"]
            ? parseInt($el.dataset["nlsProgressEnd"], 10)
            : 1;
        this.scrollPosition = $el.dataset["nlsPosition"]
            ? $el.dataset["nlsPosition"]
            : null;
        this.repeat = $el.dataset["nlsRepeat"] != undefined ? true : false;
        this.lerp = $el.dataset["nlsLerp"]
            ? parseFloat($el.dataset["nlsLerp"])
            : 0;
        this.call = $el.dataset["nlsCall"] ? $el.dataset["nlsCall"] : null;

        // States
        this.isInteractive = false;
        this.isInview = false;
        this.isSmooth = this.lerp > 0;

        // Flags
        this.isIntersected = false;
        this.isRafPlaying = false;

        // Limits
        this.intersection = {
            start: 0,
            end: 0,
        };

        // Metrics
        this.metrics = {
            offsetTop: 0,
            offsetBottom: 0,
            bcr: {},
        };

        // Scroll Values
        this.currentScrollTop = 0;

        // Smooth progress
        this.smoothProgress = {
            current: 0,
            last: 0,
        };

        // Class
        this.inViewClass = "is-inview";

        // Setup
        this.init();
    }

    ////////////////////
    // Lifecycle
    ////////////////////
    /**
     * Init
     */
    init() {
        // First resize to compute values
        this.onResize();
    }

    /**
     * Destroy
     */
    destroy() {
        // Stop Request Animation Frame
        this.stopRaf();
    }

    ////////////////////
    // Controls
    ////////////////////
    /**
     * Start RAF
     * (Only for smooth progress)
     */
    playRaf() {
        if (this.isRafPlaying) return;
        this.isRafPlaying = true;
        this._onUpdate = this._onUpdate.bind(this);
        Raf.add(this._onUpdate);
    }
    /**
     * Stop RAF
     * (Only for smooth progress)
     */
    stopRaf() {
        if (!this.isRafPlaying) return;
        this.isRafPlaying = false;
        Raf.remove(this._onUpdate);
    }

    ////////////////////
    // Callbacks
    ////////////////////
    /**
     * Scroll callback
     */
    onScroll() {
        if (!this.isInteractive) return;
        this.currentScrollTop = getScrollTop();
        if (!this.isSmooth) {
            this._computeProgress();
        }
    }

    /**
     * Resize callback
     */
    onResize() {
        this.metrics.bcr = this.$view.getBoundingClientRect();
        this.currentScrollTop = getScrollTop();
        this._setMetrics();
        this._setIntersection();

        if (this.isInteractive && !this.isSmooth) {
            this._computeProgress();
        }
    }

    /**
     * Update callback
     * (Only for smooth progress)
     */
    _onUpdate() {
        this._computeProgress();
    }

    ////////////////////
    // Setters
    ////////////////////
    /**
     * Compute metrics
     */
    _setMetrics() {
        this.metrics.offsetTop = this.currentScrollTop + this.metrics.bcr.top;
        this.metrics.offsetBottom =
            this.metrics.offsetTop + this.metrics.bcr.height;
    }

    /**
     * Compute custom intersection values like root margin
     */
    _setIntersection() {
        const viewportStart =
            window.innerHeight -
            window.innerHeight * this.intersectionStart * 0.01;
        const viewportEnd = window.innerHeight * this.intersectionEnd * 0.01;

        this.intersection = {
            start:
                this.scrollPosition === "top"
                    ? this.metrics.offsetTop
                    : this.metrics.offsetTop - viewportStart,
            end:
                this.scrollPosition === "bottom"
                    ? this.metrics.offsetBottom - viewportEnd - this.metrics.bcr.height - (window.innerHeight - this.metrics.bcr.height)
                    : this.metrics.offsetBottom - viewportEnd,
        };
    }

    /**
     * Set Interactive State
     * @param {boolean} : if is inview (IO based)
     */
    setInteractiveState(isInteractive) {
        this.isInteractive = isInteractive;

        if (this.isInteractive && this.isSmooth) {
            this.playRaf();
        } else {
            this.stopRaf();
        }

        // First time to compute progress
        if (!this.isIntersected) {
            this.isIntersected = true;
            if (!this.isSmooth) {
                this._computeProgress();
            }
        }
    }

    /**
     * Set Css Progress
     * @param {number} : computed progress
     * Set css var on view HTMLElement
     */
    _setCssProgress(currentProgress = 0) {
        if(window.isMobile) return
        this.$view.style.setProperty("--nls-progress", currentProgress);
    }

    /**
     * Set Inview Class
     * @param {boolean} : if is inview (progress based)
     */
    _setInviewClass(isInview) {
        this.isInview = isInview;

        if (this.isInview) {
            this.$view.classList.add(this.inViewClass);
        } else {
            this.$view.classList.remove(this.inViewClass);
        }
    }

    ////////////////////
    // Progress
    ////////////////////
    /**
     * Compute progress
     */
    _computeProgress() {
        const defaultProgress = clamp(
            mapRange(
                this.currentScrollTop,
                this.intersection.start,
                this.intersection.end,
                0,
                1
            ),
            0,
            1
        );
        const progress = this.isSmooth
            ? this._getSmoothProgress(defaultProgress)
            : defaultProgress;
        let currentProgress = clamp(
            mapRange(progress, 0, 1, this.progressStart, this.progressEnd),
            this.progressStart,
            this.progressEnd
        );
        currentProgress = ((1000 * currentProgress) | 0) / 1000;

        // Set css progress
        this._setCssProgress(currentProgress);

        // Set inview class
        if (progress > 0 && progress < 1 && !this.isInview) {
            this._setInviewClass(true);
            this._dispatchScrollCall(true);
        } else if (progress === 1 && this.isInview && this.repeat) {
            this._setInviewClass(false);
            this._dispatchScrollCall(false);
        } else if (progress === 0 && this.isInview && this.repeat) {
            this._setInviewClass(false);
            this._dispatchScrollCall(false);
        }
    }

    /**
     * Compute smooth progress
     * @param {number} : default progress
     * @return {number} : lerp progress
     */
    _getSmoothProgress(progress) {
        this.smoothProgress.last = this.smoothProgress.current;
        return (this.smoothProgress.current +=
            (progress - this.smoothProgress.current) * this.lerp);
    }

    ////////////////////
    // Scroll Call
    ////////////////////
    /**
     * Dispatch scroll call
     * @param {boolean} : if is inview (progress based)
     * Launch data-nls-call function
     */
    _dispatchScrollCall(isInview) {
        if (isInview && this.call) {
            ScrollCall.dispatch(this.call, "enter", this?.IOEntry);
        } else if (this.call) {
            ScrollCall.dispatch(this.call, "exit", this?.IOEntry);
        }
    }
}
